import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReportTable from "../../common/ReportTable/ReportTable";
import DateRangeFilter from "../../common/DateRangeFilter";
import DocumentPreviewModal from "../../common/DocumentPreview/DocumentPreviewModal";
import QmsModalPortal from "../qms/QmsModalPortal";
import {
  assignDocumentLibraryStaff,
  assignMyLearningStaff,
  fetchStaffDocumentLibraryDocuments,
  fetchStaffMyLearningDocuments,
  fetchWorkspaceDocumentById,
  removeDocumentLibraryStaff,
} from "../qms/qmsService";
import {
  apiMessage,
  firstSuccessStoryRow,
  getApiTable,
  getPaginationInfo,
  isApiSuccess,
  pickApiRowValue,
} from "../qms/qmsApiUtils";
import { getVaultDocumentInfo } from "../qms/qmsVaultWorkspaceUtils";
import { getVaultDocumentListId } from "../qms/qmsVaultUtils";
import { inferFileExtension } from "../../common/DocumentPreview/documentUrlUtils";
import utils from "../../../utils/utils";
import "../qms/QmsPages.scss";
import "./UserStaffDocumentAccessModal.scss";

const DEFAULT_PAGE_SIZE = 25;

const EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

const COLUMN_DATE_PRESETS = [
  "Last 30 Days",
  "Last 90 Days",
  "This Year",
  "All Time",
  "Custom",
];

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "assigned", label: "Assigned" },
  { value: "unassigned", label: "Unassigned" },
];

const ACCESS_MODES = {
  library: {
    title: "Document Library Access",
    fetchFn: fetchStaffDocumentLibraryDocuments,
    tableKey: "StaffDocumentLibraryDocuments",
    allowUnassign: true,
    noPermissionMessage:
      "This user does not have Document Library access. Assign the Document Library permission on their profile first.",
  },
  myLearning: {
    title: "My Learning Access",
    fetchFn: fetchStaffMyLearningDocuments,
    tableKey: "StaffMyLearningDocuments",
    allowUnassign: false,
    noPermissionMessage: null,
  },
};

const FIELD_OPTIONS = [
  { key: "DocumentId", label: "Id" },
  { key: "DocumentTitle", label: "Document Title" },
  { key: "PublishedDateTime", label: "Published" },
  { key: "AssignmentStatus", label: "Status" },
];

const DEFAULT_COLUMNS = [
  "DocumentId",
  "DocumentTitle",
  "PublishedDateTime",
  "AssignmentStatus",
];

const SERVER_SEARCH_FIELDS = ["DocumentId", "DocumentTitle"];

function parseDocumentIdNumberSearch(value) {
  return String(value || "").replace(/\D/g, "");
}

function getDocumentDisplayName(row) {
  const title = String(row?.DocumentTitle || "").trim();
  if (title) return title;
  const ext = row?.DocumentExtension
    ? `.${String(row.DocumentExtension).replace(/^\./, "")}`
    : "";
  return ext ? `Untitled${ext}` : "—";
}

const UserStaffDocumentAccessModal = ({
  open,
  mode = "library",
  staffRow = null,
  userData,
  showLoader,
  hideLoader,
  showAlert,
  onClose,
}) => {
  const config = ACCESS_MODES[mode] || ACCESS_MODES.library;
  const staffId = staffRow?.StaffId || "";
  const staffLabel =
    staffRow?.Name ||
    [staffRow?.FirstName, staffRow?.LastName].filter(Boolean).join(" ") ||
    "User";

  const [rows, setRows] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishedDateRange, setPublishedDateRange] = useState(EMPTY_DATE_RANGE);
  const [apiFilters, setApiFilters] = useState({
    searchText: "",
    filterDocumentIdNumber: "",
    assignmentFilter: "all",
    filterPublishedOnFrom: "",
    filterPublishedOnTo: "",
  });
  const [initialAssigned, setInitialAssigned] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [touchedIds, setTouchedIds] = useState(() => new Set());
  const [previewModal, setPreviewModal] = useState({
    open: false,
    row: null,
    documentUrl: "",
    documentExtension: "",
  });

  const fetchSeqRef = useRef(0);
  const baselineLoadedRef = useRef(false);
  const userDataRef = useRef(userData);
  userDataRef.current = userData;

  const resetState = useCallback(() => {
    baselineLoadedRef.current = false;
    setRows([]);
    setPageNumber(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setTotalCount(0);
    setTableLoading(false);
    setLoadError("");
    setSaving(false);
    setPublishedDateRange(EMPTY_DATE_RANGE);
    setApiFilters({
      searchText: "",
      filterDocumentIdNumber: "",
      assignmentFilter: "all",
      filterPublishedOnFrom: "",
      filterPublishedOnTo: "",
    });
    setInitialAssigned(new Set());
    setSelectedIds(new Set());
    setTouchedIds(new Set());
    setPreviewModal({
      open: false,
      row: null,
      documentUrl: "",
      documentExtension: "",
    });
  }, []);

  const applyAssignedBaseline = useCallback((assignedRows) => {
    const ids = assignedRows
      .map((row) => String(row?.DocumentId || ""))
      .filter(Boolean);
    if (!ids.length) return;
    setInitialAssigned((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const loadDocuments = useCallback(async () => {
    if (!open || !staffId || !userData?.OrganizationId) return;

    const seq = ++fetchSeqRef.current;
    setTableLoading(true);
    setLoadError("");

    try {
      const response = await config.fetchFn(userDataRef.current, staffId, {
        pageNumber,
        pageSize,
        searchText: apiFilters.searchText,
        filterDocumentIdNumber: apiFilters.filterDocumentIdNumber,
        assignmentFilter: apiFilters.assignmentFilter,
        filterPublishedOnFrom: apiFilters.filterPublishedOnFrom,
        filterPublishedOnTo: apiFilters.filterPublishedOnTo,
      });
      if (seq !== fetchSeqRef.current) return;

      const story = firstSuccessStoryRow(response);
      if (!isApiSuccess(response, story)) {
        const message =
          apiMessage(response, story) ||
          config.noPermissionMessage ||
          "Could not load document access.";
        setLoadError(message);
        setRows([]);
        setTotalCount(0);
        return;
      }

      setRows(getApiTable(response, config.tableKey));
      const pagination = getPaginationInfo(response);
      setTotalCount(Number(pagination.TotalCount) || 0);

      if (pageNumber === 1 && !baselineLoadedRef.current) {
        const assignedRows = getApiTable(response, "StaffAssignedDocumentIds");
        applyAssignedBaseline(assignedRows);
        baselineLoadedRef.current = true;
      }
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      setLoadError(error?.message || "Failed to load documents.");
      setRows([]);
      setTotalCount(0);
    } finally {
      if (seq === fetchSeqRef.current) {
        setTableLoading(false);
      }
    }
  }, [
    open,
    staffId,
    userData?.OrganizationId,
    pageNumber,
    pageSize,
    apiFilters,
    config,
    applyAssignedBaseline,
  ]);

  // Reset only when the modal closes (or staff/mode identity changes while closed).
  // Do NOT depend on pageNumber/apiFilters here — resetState updates those and would loop.
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, staffId, mode, resetState]);

  useEffect(() => {
    if (!open) return undefined;
    loadDocuments();
    return undefined;
  }, [open, staffId, mode, pageNumber, pageSize, apiFilters, loadDocuments]);

  const hasChanges = useMemo(() => {
    if (initialAssigned.size !== selectedIds.size) return true;
    for (const id of initialAssigned) {
      if (!selectedIds.has(id)) return true;
    }
    for (const id of selectedIds) {
      if (!initialAssigned.has(id)) return true;
    }
    return false;
  }, [initialAssigned, selectedIds]);

  const getRowKey = useCallback((row) => String(row?.DocumentId || ""), []);

  const openDocumentPreview = useCallback(
    async (row) => {
      if (!row?.DocumentId || !userData) return;

      const existingUrl = String(
        pickApiRowValue(row, "DocumentURL", "documentUrl") || "",
      ).trim();
      const listExt =
        pickApiRowValue(row, "DocumentExtension", "documentExtension") || "";

      // Staff access list APIs omit DocumentURL — resolve it like the QMS workspace does.
      if (existingUrl) {
        setPreviewModal({
          open: true,
          row,
          documentUrl: existingUrl,
          documentExtension: inferFileExtension(
            listExt,
            existingUrl,
            getDocumentDisplayName(row),
          ),
        });
        return;
      }

      try {
        showLoader?.("Loading document preview...");
        const response = await fetchWorkspaceDocumentById(
          userData,
          row.DocumentId,
        );
        const info = getVaultDocumentInfo(response);
        const url = String(
          pickApiRowValue(info, "DocumentURL", "documentUrl") || "",
        ).trim();
        const ext = inferFileExtension(
          pickApiRowValue(info, "DocumentExtension", "documentExtension") ||
            listExt,
          url,
          getDocumentDisplayName(row),
        );

        if (!url) {
          showAlert?.({
            type: "warning",
            title: "Preview unavailable",
            message: "Document URL is missing for this file.",
          });
          return;
        }

        setPreviewModal({
          open: true,
          row,
          documentUrl: url,
          documentExtension: ext,
        });
      } catch (error) {
        showAlert?.({
          type: "error",
          title: "Preview unavailable",
          message: error?.message || "Could not load document preview.",
        });
      } finally {
        hideLoader?.();
      }
    },
    [userData, showLoader, hideLoader, showAlert],
  );

  const closeDocumentPreview = useCallback(() => {
    setPreviewModal({
      open: false,
      row: null,
      documentUrl: "",
      documentExtension: "",
    });
  }, []);

  const isRowSelectable = useCallback(
    (row) => {
      if (saving) return false;
      if (!config.allowUnassign && initialAssigned.has(getRowKey(row))) {
        return false;
      }
      return true;
    },
    [config.allowUnassign, initialAssigned, getRowKey, saving],
  );

  const toggleDocumentSelection = useCallback(
    (row) => {
      const id = getRowKey(row);
      if (!id) return;
      if (!config.allowUnassign && initialAssigned.has(id)) return;

      setTouchedIds((prev) => new Set(prev).add(id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [config.allowUnassign, getRowKey, initialAssigned],
  );

  const togglePageSelection = useCallback(
    (pageRows, selectAll) => {
      const selectableRows = pageRows.filter(isRowSelectable);
      setTouchedIds((prev) => {
        const next = new Set(prev);
        selectableRows.forEach((row) => next.add(getRowKey(row)));
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        selectableRows.forEach((row) => {
          const id = getRowKey(row);
          if (!id) return;
          if (selectAll) {
            next.add(id);
          } else {
            next.delete(id);
          }
        });
        return next;
      });
    },
    [getRowKey, isRowSelectable],
  );

  const rowSelection = useMemo(
    () => ({
      selectedKeys: selectedIds,
      getRowKey,
      isRowSelectable,
      onToggleRow: toggleDocumentSelection,
      onTogglePage: togglePageSelection,
      columnWidthRem: 2.75,
    }),
    [
      selectedIds,
      getRowKey,
      isRowSelectable,
      toggleDocumentSelection,
      togglePageSelection,
    ],
  );

  const handleServerColumnSearch = useCallback((columnSearches) => {
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      searchText: String(columnSearches.DocumentTitle || "").trim(),
      filterDocumentIdNumber: parseDocumentIdNumberSearch(columnSearches.DocumentId),
    }));
  }, []);

  const handlePublishedDateRangeChange = useCallback((range) => {
    setPublishedDateRange(range);
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      filterPublishedOnFrom:
        range?.preset === "All Time" ? "" : range?.startDate || "",
      filterPublishedOnTo:
        range?.preset === "All Time" ? "" : range?.endDate || "",
    }));
  }, []);

  const handleStatusFilterChange = useCallback((value) => {
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      assignmentFilter: value || "all",
    }));
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "DocumentId") {
        if (options?.checkOnly) return true;
        return (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="report-table__column-search qms-doc-lib__column-filter"
            placeholder="Search number"
            value={parseDocumentIdNumberSearch(options?.value)}
            disabled={options?.disabled}
            onChange={(event) => {
              options?.onChange?.(parseDocumentIdNumberSearch(event.target.value));
            }}
          />
        );
      }

      if (field === "PublishedDateTime") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={publishedDateRange}
              onChange={handlePublishedDateRangeChange}
              placeholder="Published date range"
              presetOptions={COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      if (field === "AssignmentStatus") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={apiFilters.assignmentFilter}
            disabled={options?.disabled}
            onChange={(event) => handleStatusFilterChange(event.target.value)}
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      return null;
    },
    [
      apiFilters.assignmentFilter,
      handlePublishedDateRangeChange,
      handleStatusFilterChange,
      publishedDateRange,
    ],
  );

  const handleSave = async () => {
    if (!hasChanges || !staffId || saving) return;

    const toAssign = [...selectedIds].filter((id) => !initialAssigned.has(id));
    const toRemove = config.allowUnassign
      ? [...initialAssigned].filter((id) => !selectedIds.has(id))
      : [];

    if (!toAssign.length && !toRemove.length) return;

    try {
      setSaving(true);
      showLoader?.("Updating document access...");

      for (const documentId of toAssign) {
        const response =
          mode === "library"
            ? await assignDocumentLibraryStaff(userData, documentId, staffId)
            : await assignMyLearningStaff(userData, documentId, staffId);
        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "Failed to assign document.");
        }
      }

      for (const documentId of toRemove) {
        const response = await removeDocumentLibraryStaff(userData, documentId, staffId);
        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "Failed to remove document access.");
        }
      }

      showAlert?.({
        title: "Updated",
        message:
          mode === "library"
            ? "Document library access updated. Notification email(s) have been sent to the user."
            : "My Learning access updated. Notification email(s) have been sent to the user.",
        type: "success",
      });
      onClose?.();
    } catch (error) {
      showAlert?.({
        title: "Error",
        message: error?.message || "Failed to update document access.",
        type: "danger",
      });
    } finally {
      setSaving(false);
      hideLoader?.();
    }
  };

  const renderReportCell = useCallback(
    (row, field) => {
      const id = getRowKey(row);
      const isSelected = selectedIds.has(id);

      if (field === "DocumentId") {
        const listId = getVaultDocumentListId(row);
        return (
          <span className="qms-doc-lib__doc-id" title={listId}>
            {listId}
          </span>
        );
      }
      if (field === "DocumentTitle") {
        const name = getDocumentDisplayName(row);
        return (
          <button
            type="button"
            className="report-table__cell-link qms-doc-lib__doc-title-link"
            title={`Preview ${name}`}
            onClick={() => openDocumentPreview(row)}
          >
            {name}
          </button>
        );
      }
      if (field === "PublishedDateTime") {
        return utils.formatDateTimeDisplay(row.PublishedDateTime) || "—";
      }
      if (field === "AssignmentStatus") {
        return (
          <span
            className={`qms-doc-lib__status-badge ${
              isSelected ? "is-assigned" : "is-unassigned"
            }`}
          >
            {isSelected ? "Assigned" : "Unassigned"}
          </span>
        );
      }
      return String(row[field] ?? "—");
    },
    [getRowKey, openDocumentPreview, selectedIds],
  );

  const serverPagination = useMemo(
    () => ({
      page: pageNumber,
      pageSize,
      totalCount,
      loading: tableLoading,
      onPageChange: setPageNumber,
      onPageSizeChange: (size) => {
        setPageNumber(1);
        setPageSize(size);
      },
    }),
    [pageNumber, pageSize, totalCount, tableLoading],
  );

  if (!open) return null;

  return (
    <QmsModalPortal>
      <div
        className="modal fade show d-block user-staff-doc-access-modal qms-document-library"
        role="dialog"
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-0">{config.title}</h5>
                <div className="user-staff-doc-access-modal__subtitle">{staffLabel}</div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>

            <div className="modal-body">
              {!loadError ? (
                <div className="qms-doc-lib__panel user-staff-doc-access-modal__table-panel">
                  <div className="qms-doc-lib__report-wrap">
                    <ReportTable
                      rows={rows}
                      fieldOptions={FIELD_OPTIONS}
                      defaultColumns={DEFAULT_COLUMNS}
                      getFieldLabel={(field) =>
                        FIELD_OPTIONS.find((x) => x.key === field)?.label || field
                      }
                      renderCell={renderReportCell}
                      renderColumnSearch={renderColumnSearch}
                      pageSizeOptions={[10, 25, 50, 100]}
                      serverPagination={serverPagination}
                      columnSearchMode="server"
                      serverColumnSearch={{
                        searchableFields: SERVER_SEARCH_FIELDS,
                        debounceMs: 400,
                        onChange: handleServerColumnSearch,
                      }}
                      disableColumnLayout
                      hideAddColumnFields={DEFAULT_COLUMNS}
                      hideColumnHeaderActionsFields={DEFAULT_COLUMNS}
                      frozenColumnWidthRem={8}
                      fitToContainer
                      rowSelection={rowSelection}
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="modal-footer user-staff-doc-access-modal__footer">
              {loadError ? (
                <p
                  className="user-staff-doc-access-modal__footer-note user-staff-doc-access-modal__footer-note--warning mb-0"
                  role="alert"
                >
                  {loadError}
                </p>
              ) : null}
              {!loadError && mode === "myLearning" ? (
                <p className="user-staff-doc-access-modal__footer-note mb-0" role="status">
                  Already assigned documents cannot be unassigned from here. You can assign
                  additional published documents only.
                </p>
              ) : null}
              <div className="user-staff-doc-access-modal__footer-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
                  Close
                </button>
                {!loadError ? (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!hasChanges || saving || tableLoading}
                    onClick={handleSave}
                  >
                    Save changes
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />

      <DocumentPreviewModal
        open={previewModal.open}
        onClose={closeDocumentPreview}
        sourceModule="QMS"
        sourceTable="DMS_MyVault_Documents"
        documentId={String(previewModal.row?.DocumentId || "")}
        documentUrl={previewModal.documentUrl || ""}
        documentExtension={previewModal.documentExtension || ""}
        documentTitle={
          previewModal.row
            ? getDocumentDisplayName(previewModal.row)
            : "Document"
        }
        documentCode={
          previewModal.row ? getVaultDocumentListId(previewModal.row) : ""
        }
      />
    </QmsModalPortal>
  );
};

export default UserStaffDocumentAccessModal;
